/**
 * Client-side photo checks of the menu capture (plan §10.3): a small grey
 * copy of the photo is measured for brightness and sharpness, and the tray
 * warns about small, dark or blurry photos. Warnings never block a read.
 */

/**
 * Uncalibrated defaults: the AI bench calibrates them with the owner's
 * fixture photos (§9.7, D7), which has not run yet. Change them only with
 * that bench run.
 */
export const PHOTO_CHECK_THRESHOLDS = {
  /** Short side, in pixels, of the photo that is uploaded. */
  minShortSide: 900,
  /** Mean BT.601 luminance, 0..255. */
  minLuminance: 55,
  /** Variance of the 3×3 Laplacian of the grey image. */
  minSharpness: 60,
} as const;

export interface IPixelAnalysis {
  /** Mean luminance, 0..255. */
  luminance: number;
  /** Variance of the Laplacian over the interior pixels (0 for images under 3×3). */
  sharpness: number;
}

export type TPhotoWarning = 'none' | 'small' | 'dark' | 'blurry';

/** BT.601 luma of every pixel of an RGBA buffer. */
function greyscale(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): Float64Array {
  const grey = new Float64Array(width * height);
  for (let i = 0; i < grey.length; i++) {
    const offset = i * 4;
    grey[i] =
      0.299 * rgba[offset] +
      0.587 * rgba[offset + 1] +
      0.114 * rgba[offset + 2];
  }
  return grey;
}

/**
 * Mean luminance and sharpness of an RGBA buffer (`ImageData.data`). The
 * sharpness is the variance of the Laplacian `0 1 0 / 1 −4 1 / 0 1 0`: a
 * blurry photo has soft edges and a low variance.
 */
export function analyzePixels(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): IPixelAnalysis {
  const pixels = width * height;
  if (width <= 0 || height <= 0 || rgba.length < pixels * 4)
    return { luminance: 0, sharpness: 0 };

  const grey = greyscale(rgba, width, height);
  let total = 0;
  for (let i = 0; i < grey.length; i++) total += grey[i];
  const luminance = total / pixels;

  if (width < 3 || height < 3) return { luminance, sharpness: 0 };

  let sum = 0;
  let sumOfSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const laplacian =
        grey[i - width] +
        grey[i - 1] +
        grey[i + 1] +
        grey[i + width] -
        4 * grey[i];
      sum += laplacian;
      sumOfSquares += laplacian * laplacian;
      count++;
    }
  }
  const mean = sum / count;
  // Clamped: rounding can leave a flat image a hair below zero.
  return {
    luminance,
    sharpness: Math.max(0, sumOfSquares / count - mean * mean),
  };
}

/** The tray warning of a processed photo, by priority: small, dark, blurry. */
export function photoWarning(
  analysis: IPixelAnalysis,
  shortSide: number
): TPhotoWarning {
  if (shortSide < PHOTO_CHECK_THRESHOLDS.minShortSide) return 'small';
  if (analysis.luminance < PHOTO_CHECK_THRESHOLDS.minLuminance) return 'dark';
  if (analysis.sharpness < PHOTO_CHECK_THRESHOLDS.minSharpness) return 'blurry';
  return 'none';
}
